/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ClusterId } from './cluster';
import { DrSenderGroupName, FullStateTransferDrStatus, IncrementalDrStatus, IncrementalDrStatusReason } from './dr';

export type CacheName = string;
export type DashboardGroupName = string;
export type DashboardId = string;
export type UserId = string;
export type UserEmail = string;
export type PartitionId = number;
export type Timestamp = number;
export type TimestampInMs = number;

export interface User {
  id: UserId;
  admin: boolean;
  company?: string | null;
  country: string;
  email: UserEmail;
  firstName?: string | null;
  industry?: string | null;
  lastName?: string | null;
  permitEmailContact: boolean;
  permitPhoneContact: boolean;
  phone: string;
  token: string;
  oidcProvider: string | null;
  currentSessionIP: string;
  jobTitle?: string | null;
}

export interface SignupUserData {
  email: string;
  password?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
  country: string | null;
  permitEmailContact?: boolean | null;
  permitPhoneContact?: boolean | null;
  adminCreationToken?: string | null;
  jobTitle?: string | null;
}

export interface EditUserData {
  email: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  country: string;
  permitEmailContact?: boolean;
  permitPhoneContact?: boolean;
  adminCreationToken?: string;
  jobTitle?: string;
  admin?: boolean | null;
  userLocked?: boolean;
}

export type GmcStompErrorCode =
  | 'err.account-not-auth-for-subscribe-on-topic'
  | 'err.account-have-not-attached-cluster'
  | 'err.incorrect-cluster-id'
  | 'err.cluster-is-not-attached';

export interface GmcStompUnknownError {
  code: null;
  message: null | string;
}
export interface GmcStompKnownError {
  code: GmcStompErrorCode;
  message: string;
}
export type GmcStompError = GmcStompKnownError | GmcStompUnknownError;

export enum CacheMode {
  LOCAL = 'LOCAL',
  REPLICATED = 'REPLICATED',
  PARTITIONED = 'PARTITIONED',
}

export enum CacheAtomicityMode {
  ATOMIC = 'ATOMIC',
  TRANSACTIONAL = 'TRANSACTIONAL',
  TRANSACTIONAL_SNAPSHOT = 'TRANSACTIONAL_SNAPSHOT',
}

export type CacheGroupName = string;

export type CacheId = string;

export interface IgniteCache {
  name: CacheName;
  cacheId: CacheId;
  createdBySql: boolean;
  systemCache: boolean;
  group?: CacheGroupName;
  mode: CacheMode | null;
  atomicityMode: CacheAtomicityMode | null;
  statisticsEnabled: boolean | null;
  encryptionEnabled?: boolean;
  drSenderGroupName: DrSenderGroupName | null;
  incrementalDataReplicationStatus: IncrementalDrStatus;
  incrementalDataReplicationStatusReason: IncrementalDrStatusReason;
  fullStateDataReplicationStatus: FullStateTransferDrStatus;
}

export type GroupedCacheNames = {
  // cache names without group will be in group with undefiled name
  group: CacheGroupName | undefined;
  caches: CacheName[];
};

export interface DashboardGroup {
  id: string;
  name: DashboardGroupName;
  workspaceIds: DashboardId[];
}

export enum WidgetType {
  metricsGrid = 'widget-metrics-grid-v2',
  metricsChart = 'widget-node-metrics-chart-v2',
  metricsHistogram = 'widget-node-metrics-histogram-v2',
  heatMap = 'widget-nodes-heat-map-v2',
  gaugeChart = 'widget-gauge-chart',
  nodes = 'widget-nodes',
  rebalance = 'widget-rebalance',
  caches = 'widget-caches',
  remoteReceiverConnections = 'widget-remote-receiver-connections',
  remoteSenders = 'widget-remote-senders', // Keep in sync with the backend when renaming
  metricSingleValue = 'widget-metric-single-value',
  systemView = 'widget-system-view',
  sqlQuery = 'widget-sql-query',
}

export type ContactUsRequest = {
  name: string | null;
  email: string;
  clusterId: ClusterId | null;
  subject: string | null;
  message: string;
};

export type AgentVersionType = {
  version: string;
  link: string;
};

export type AgentVersionLinksResponseBody = AgentVersionType[];

/**
 *  @summary RFC4122 UUID
 * @example 9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d
 */
export type UuidV4 = string;

/**
 *  @summary Ignite UUID.
 * @description This is a faster performing version of UUID. Constructs from a global and local identifiers.
 * @example 47e1fdd72b4-057e5efa-539c-4383-8853-347abeab33da.
 * @link https://ignite.apache.org/releases/latest/javadoc/org/apache/ignite/lang/IgniteUuid.html
 */
export type IgniteUUID = string;

export type PaginatedDataResponse<T> = {
  data: T[];
  count: number | undefined;
};

export type CsvString = string;
export interface ImportUsersResponse {
  totalNumberOfUsers: Integer;
  registered: Integer;
  failed: Integer;
  failUsers: CsvString;
}

export type Integer = number;
export type SignedInteger = Integer;
export const SIGNED_INTEGER_MAX_VALUE = 2147483647;
